<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue'
import { Button } from "@/shadcn/components/ui/button";
import Alert from "@/components/ui/Alert.vue";
import {
  CloudCheckIcon,
  CloudAlertIcon,
  CircleXIcon,
} from "@lucide/vue"

type ThirdPartyService = {
  id: number,
  name: string,
  service: string,
}

const props = defineProps<{
  thirdPartyService: ThirdPartyService,
  model: string,
  modelId: number | string,
}>()

type SynchronizationStatus = {
  is_synchronized: boolean,
  response: any,
  synchronized_at: string | null,
}

type ApiError = {
  message: string,
}

type SyncResponse = {
  message: string,
  response?: any,
}

const synchronizationStatus = ref<SynchronizationStatus | null>(null);
const isLoading = ref(false);
const isSyncing = ref(false);
const isPolling = ref(false);
const error = ref<string | null>(null);
const syncSuccess = ref<string | null>(null);

let pollingIntervalId: ReturnType<typeof setInterval> | null = null;

const buildParams = (): URLSearchParams => {
  const params = new URLSearchParams();
  params.append('third_party_service_id', String(props.thirdPartyService.id))
  params.append('model', props.model)
  params.append('model_id', props.modelId.toString())
  return params;
}

const stopPolling = () => {
  if (pollingIntervalId) {
    clearInterval(pollingIntervalId);
    pollingIntervalId = null;
  }
  isPolling.value = false;
}

const fetchStatus = async (silent = false) => {
  if (!silent) {
    isLoading.value = true;
  }
  error.value = null;

  try {
    const response = await fetch(`/backoffice/api/thirdparty-synchronization?` + buildParams(), {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle 400, 404 and other error responses
      const errorData = data as ApiError;
      error.value = errorData.message || `Error ${response.status}: ${response.statusText}`;
      synchronizationStatus.value = null;
      stopPolling();
      return;
    }

    synchronizationStatus.value = data as SynchronizationStatus;

    // Stop polling if synchronized
    if (synchronizationStatus.value.is_synchronized) {
      stopPolling();
    }
  } catch (err) {
    console.error('Error fetching synchronization status:', err);
    error.value = 'Failed to fetch synchronization status. Please try again.';
    synchronizationStatus.value = null;
    stopPolling();
  } finally {
    if (!silent) {
      isLoading.value = false;
    }
  }
}

const startPolling = () => {
  stopPolling(); // Clear any existing interval
  isPolling.value = true;

  pollingIntervalId = setInterval(() => {
    fetchStatus(true); // Silent fetch to avoid loading state flicker
  }, 1000);
}

onMounted(() => {
  fetchStatus();
});

onUnmounted(() => {
  stopPolling();
});

const sync = async () => {
  isSyncing.value = true;
  error.value = null;
  syncSuccess.value = null;

  try {
    const response = await fetch(`/backoffice/api/thirdparty-synchronization?` + buildParams(), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement).content
      }
    });

    const data = await response.json();

    if (!response.ok) {
      // Handle 400, 404 and other error responses
      const errorData = data as ApiError;
      error.value = errorData.message || `Error ${response.status}: ${response.statusText}`;
      return;
    }

    /*
    const syncData = data as SyncResponse;
    syncSuccess.value = syncData.message;
     */

    // Start polling to check for synchronization completion
    startPolling();
  } catch (err) {
    console.error('Error during synchronization:', err);
    error.value = 'Failed to synchronize. Please try again.';
  } finally {
    isSyncing.value = false;
  }
}

const clearMessages = () => {
  error.value = null;
  syncSuccess.value = null;
}


</script>
<template>
  <div class="card mb-3">
    <div class="card-header tw:flex tw:items-center tw:gap-2">
      <span class="tw:font-bold">
        {{ thirdPartyService.service }}
      </span>
      <span class="tw:text-zinc-500">
        {{ thirdPartyService.name }}
      </span>
    </div>
    <div class="card-body">
      <!-- Error Message -->
      <Alert
        v-if="error"
        variant="danger"
        :icon="CircleXIcon"
        show-icon
        dismissible
      >
        {{ error }}
      </Alert>

      <!-- Success Message -->
      <Alert
        class="tw:mb-0"
        variant="success"
        v-if="syncSuccess"
      >
        {{ syncSuccess }}
      </Alert>

      <!-- Loading State -->
      <div
        v-if="isLoading"
        class="tw:flex tw:items-center tw:gap-2 tw:text-gray-500"
      >
        <span class="tw:animate-spin tw:w-4 tw:h-4 tw:border-2 tw:border-gray-300 tw:border-t-gray-600 tw:rounded-full"></span>
        <span class="tw:text-sm">{{ $t('Loading synchronization status…') }}</span>
      </div>

      <!-- Synchronization Status -->
      <template v-else-if="synchronizationStatus">
        <Alert
          class="tw:mb-0"
          variant="success"
          :icon="CloudCheckIcon"
          show-icon
          v-if="synchronizationStatus.is_synchronized"
        >
          <strong>{{ $t('Synchronized') }}</strong>
          &nbsp;
          <span v-if="synchronizationStatus.synchronized_at">
            {{ $t('at {date}', {date: synchronizationStatus.synchronized_at }) }}
          </span>
        </Alert>

        <Alert
          class="tw:mb-0"
          variant="info"
          v-else-if="isPolling"
        >
          <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
            <div class="tw:flex tw:items-center tw:gap-2">
              <span class="tw:animate-spin tw:w-4 tw:h-4 tw:border-2 tw:border-blue-400 tw:border-t-blue-600 tw:rounded-full"></span>
              <strong class="tw:text-blue-700 dark:tw:text-blue-400">
                {{ $t('Synchronization in progress…') }}
              </strong>
            </div>
            <Button
              variant="outline"
              size="sm"
              @click="stopPolling"
            >
              {{ $t('Cancel') }}
            </Button>
          </div>
          <p class="tw:text-xs tw:text-blue-600 tw:mt-2 dark:tw:text-blue-400">
            {{ $t('Waiting for server to complete synchronization…') }}
          </p>
        </Alert>

        <Alert
          class="tw:mb-0"
          variant="warning"
          :icon="CloudAlertIcon"
          show-icon
          v-else
        >
          <div class="tw:flex tw:items-center tw:justify-between tw:gap-4">
            <strong>{{ $t('Not Synchronized') }}</strong>
            <Button
              variant="outline"
              :disabled="isSyncing"
              :loading="isSyncing"
              size="sm"
              @click="sync"
            >
              <span v-if="isSyncing">
                {{ $t('Synchronizing…') }}
              </span>
              <span v-else>
                {{ $t('Synchronize now') }}
              </span>
            </Button>
          </div>
        </Alert>

        <!-- Response Details -->
        <details
          v-if="synchronizationStatus.response"
          class="tw:mt-2"
        >
          <summary class="tw:text-sm tw:text-gray-500 tw:cursor-pointer hover:tw:text-gray-700 dark:tw:text-gray-400 dark:hover:tw:text-gray-300">
            {{ $t('View response details') }}
          </summary>
          <pre class="tw:mt-2 tw:p-3 tw:bg-gray-100 tw:rounded-md tw:text-xs tw:overflow-auto tw:max-h-48 dark:tw:bg-gray-800">{{ JSON.stringify(synchronizationStatus.response, null, 2) }}</pre>
        </details>
      </template>

      <!-- No Status Available (when not loading and no error) -->
      <div
        v-else-if="!error"
        class="tw:text-sm tw:text-gray-500"
      >
        {{ $t('No synchronization status available.') }}
      </div>
    </div>
  </div>
</template>